import { groq } from "next-sanity"

import { CommerceService } from "@/services/commerce"
import { EinzaGraphiQLService } from "@/services/einzaGraphiQL"

import { canAccessPurchasePrice } from "../permissions/permissions"
import { groqArticleProjection } from "../sanity/article.projection"
import { getArticleProjections, getEnrichedArticles, getProductProjectionAttributes } from "./getArticleInformation"
import { getSkuPricesFromSkuArray } from "./getPrices"
import { canAccessCartDbValues, getAvgDbBySku } from "./getRegionStatistics"
import { getListingByBranch } from "./getVariantListing"
import { groqQueryOptions } from "./groqQueryOptions"

const { draftsFilter } = groqQueryOptions({ includeDrafts: true })

/**
 * Method to retrieve top articles by einza graphiql response
 *
 * @param {[object]} data
 * @param {SanityClient} client
 *
 * @return {Promise<array>}
 * @throws {Error}
 */
export const getTopArticles = async (data, client) => {
  const skus = data.map(({ sku }) => sku)

  const articles = await getArticlesFromSkuArray(client, skus)

  if (!articles || articles.length <= 0) {
    throw new Error("No articles were found")
  }

  // Enrich articles with sellableInDecimals and other commercetools data
  const enrichedArticles = await getEnrichedArticles({
    articles,
    branchId: null,
    customerNumber: null,
    customerId: null,
  })

  let dataByArticle = data.map((items) => {
    const article = enrichedArticles.find((article) => article.sku === items.sku)

    if (!article) {
      return null
    }

    return {
      ...items,
      article,
    }
  })

  // remove all nulls
  dataByArticle = dataByArticle.filter((n) => n)

  if (dataByArticle.length <= 0) {
    throw new Error("No articles could be matche with available data")
  }

  return dataByArticle
}

/**
 * Method to retrieve top articles by einza graphiql response
 *
 * @param {object} cart
 * @param {SanityClient} client
 * @param {string} branchKey
 * @param {boolean} enriched
 * @param {object} user
 * @param {string} customerNumber
 * @param {string} [regionSanityId]
 *
 * @return {Promise<object|null>}
 */
export const getArticlesFromCart = async (cart, client, branchKey, enriched, user, customerNumber, regionSanityId) => {
  // in case no line items are available
  if (!cart) return null
  if (!cart?.customLineItems || cart?.customLineItems?.length <= 0) return cart

  const { customLineItems } = cart

  const skus = [...new Set(customLineItems.map(({ custom }) => custom?.fields?.sku).filter(Boolean))]

  if (skus.length <= 0) return cart

  const articles = await getArticlesFromSkuArray(client, skus)
  const articleListing = await getListingByBranch({ variants: articles, branchKey })
  const listingsChargenData = await getChargenDataFromListings(articleListing)

  if (!articles || articles.length <= 0) return cart

  const articleProjections = await getArticleProjections({ skus })
  const skusListPrices = await getSkuPricesFromSkuArray({ skus })

  let avgDbBySku = new Map()

  if (regionSanityId && canAccessCartDbValues(user?.role)) {
    avgDbBySku = await getAvgDbBySku({
      articleProjections,
      skus,
      regionSanityId,
    })
  }

  const minimumPriceKeys = skus.map((sku) => `"${sku}-minimumSellingPrice"`).join(", ")

  const commerceApi = CommerceService.api()

  const articlesMinimumPrices = (
    await commerceApi
      .standalonePrices()
      .get({ queryArgs: { where: `key in (${minimumPriceKeys})`, limit: 500 } })
      .execute()
  )?.body?.results

  const updatedCustomLineItems = await Promise.all(
    customLineItems.map(async (customLineItem) => {
      const customLineItemSku = customLineItem?.custom?.fields?.sku

      const article = articles?.find((article) => customLineItemSku === article?.sku)
      const listing = articleListing?.find((listing) => listing?.variantSku === customLineItemSku)

      // in case article was not found
      if (!article) return customLineItem

      const articleProjection = articleProjections.find((projection) => {
        if (projection?.masterVariant?.sku === customLineItemSku) return true
        if (projection?.variants?.find((variant) => variant?.sku === customLineItemSku)) return true

        return false
      })

      const articleConfig = getProductProjectionAttributes(articleProjection, customLineItemSku)
      const chargenData = listingsChargenData?.filter((chargen) =>
        listing?.chargenData?.map((listingChargen) => listingChargen?.id)?.includes(chargen?.id),
      )

      let listPrices = null
      let grossSellingPrice = null
      let purchasePrice = null
      let pastOrders = null
      let pastReservations = null

      if (enriched) {
        listPrices = skusListPrices?.filter((price) => price?.sku === customLineItemSku)?.filter((n) => n)

        const hasPurchasePriceAccess = canAccessPurchasePrice(user?.role)

        // Keep the visible purchase-price list entry (EK Preis) restricted, but still expose the value for DB calculations
        if (!hasPurchasePriceAccess) {
          listPrices = listPrices?.filter((price) => !price?.key?.includes("purchasePrice"))
        }

        purchasePrice = articleConfig?.articlePurchasePrice
        grossSellingPrice = articleConfig?.articleSuggestedSellingPriceGross

        pastOrders = await getPastOrders({
          customerNumber,
          sku: customLineItemSku,
        })
        pastReservations = await getPastOrders({
          customerNumber,
          sku: customLineItemSku,
          orderType: "RB",
        })
      }

      const minSellingPrice =
        articlesMinimumPrices && articlesMinimumPrices.length > 0
          ? articlesMinimumPrices.find((price) => price.sku === `${customLineItemSku}`)
          : null

      return {
        ...customLineItem,
        article,
        listing: {
          availability: listing?.availability || "unknown",
          availableQuantity: listing?.custom?.fields?.adm_availableQuantity,
          quantityOnStock: listing?.custom?.fields?.adm_availableQuantity,
          reservedQuantity: listing?.custom?.fields?.adm_availableQuantity,
        },
        amountBilling: articleConfig?.amountBilling,
        sellableInDecimals: articleConfig?.sellableInDecimals || false,
        minSellingPrice: minSellingPrice?.value || null,
        chargenData: chargenData || [],
        listPrices,
        purchasePrice,
        grossSellingPrice,
        pastOrders,
        pastReservations,
        avgDb: avgDbBySku.get(customLineItemSku) ?? null,
      }
    }),
  )

  return {
    ...cart,
    customLineItems: updatedCustomLineItems,
  }
}

/**
 * Provided array of skus, retrieves all articles from sanity
 *
 * @param {SanityClient} client
 * @param {[string]} skus
 *
 * @returns {Promise<[articles]>}
 */
export const getArticlesFromSkuArray = async (client, skus) => {
  const articles = await client.fetch(
    groq`*[_type=="article" && sku in $skus ${draftsFilter}] {
      ...,
      ${groqArticleProjection()}
    }`,
    { skus },
  )

  return articles
}

/**
 * Gets chargen data for modal display
 *
 * @param {{listChargen: array}}
 *
 * @returns {Promise<array>}
 */
export const getChargenData = async ({ listChargen }) => {
  if (!listChargen || listChargen?.length === 0) return []

  const chargenIds = listChargen?.map((chargen) => `"${chargen.id}"`).join(", ")

  if (!chargenIds) return []

  const commerceApi = CommerceService.api()

  const chargens = (
    await commerceApi
      .customObjects()
      .withContainer({ container: "chargenDataContainer" })
      .get({ queryArgs: { where: `id in (${chargenIds})` } })
      .execute()
  )?.body?.results

  return formChargenData(chargens)
}

/**
 * Forms charges data
 *
 * @param {array} chargens
 *
 * @returns {array<{chargenNo: string, availableQuantity: number}>}
 */
export const formChargenData = (chargens) => {
  if (!chargens || chargens.length === 0) return []

  return chargens.map((chargen) => {
    const chargenData = JSON.parse(chargen.value)

    return {
      id: chargen?.id,
      chargenNo: chargenData?.chargenNo || "00",
      availableQuantity: chargenData?.adm_availableQuantity || 0,
    }
  })
}

/**
 * Retrieves past orders for the given customer, sku and order type
 *
 * @param {{customerNumber: string, sku: string, orderType: string}}
 *
 * @returns {Promise<array|null>}
 */
export const getPastOrders = async ({ customerNumber, sku, orderType = "AN" }) => {
  if (!customerNumber || !sku) return null

  const size = 1
  const page = 0
  const sort = { orderBy: "orderDate", direction: "desc" }
  const filterValues = [
    { field: "orderType", operator: "eq", value: orderType },
    { field: "item.sku", operator: "eq", value: sku },
  ]

  const method = "orders: findOrdersByCustomerAndFilter"

  const graphiQLQueryObject = [
    {
      method,
      variables: {
        customerNumber,
        filters: filterValues,
        page,
        size,
        sort: EinzaGraphiQLService.formatSortParam(sort),
      },
      fields: ["numberOfElements"],
      expandedFields: {
        content: [
          "branchNumber",
          "orderNumberLong: orderNumber",
          "orderNumberShort: orderNumber2",
          "customerNumber",
          "orderDate",
          "validTill",
          "items {\nsku\nprice\ndiscount\n}",
        ],
      },
    },
  ]

  try {
    const mappedQuery = EinzaGraphiQLService.formQuery(graphiQLQueryObject)

    const customerOrdersResponse = (await EinzaGraphiQLService.getData(mappedQuery))?.data?.orders

    if (!customerOrdersResponse || customerOrdersResponse.length <= 0) {
      return null
    }

    return customerOrdersResponse?.content
  } catch (error) {
    console.error("Past orders error", { error, customerNumber, sku, orderType })
    return null
  }
}

const getChargenDataFromListings = async (articleListings) => {
  if (!articleListings || articleListings?.length === 0) return []

  const chargenIds = articleListings.flatMap((listing) => listing?.chargenData).filter((n) => n)

  if (!chargenIds) return []

  let results = []
  let offset = 0
  let limit = 500
  let total = chargenIds.length
  let currentLastIndex = limit

  const commerceApi = CommerceService.api()

  do {
    const chargens = []

    for (let index = offset; index < currentLastIndex; index++) {
      if (chargenIds[index] && chargenIds[index]?.id) {
        chargens.push(`"${chargenIds[index].id}"`)
      }
    }

    if (chargens.length === 0) return []

    const response = (
      await commerceApi
        .customObjects()
        .withContainer({ container: "chargenDataContainer" })
        .get({ queryArgs: { where: `id in (${chargens.join(",")})`, limit } })
        .execute()
    )?.body?.results

    if (!response || response.results?.length === 0) return results

    results = results.concat(response)
    offset += limit
    currentLastIndex = limit + currentLastIndex < total ? currentLastIndex + limit : total
  } while (results.length < total)

  return formChargenData(results)
}
